<template lang="pug">
footer#footer(:class="{ 'service-main-footer': currentRoutePath === 'my-projects' && currentService }")
        .bq-ft-wrap
            .bq-ft-left
                .bq-ft-brand
                    router-link.bq-ft-logo(to="/") BunnyQuery
                    span.bq-ft-version(translate="no") v{{ APP_VERSION }}
                .bq-ft-info
                    a.bq-ft-terms(href="/tspp-bq.html" target="_blank" data-track="footer_terms_of_service") Terms of service / Privacy policy
                    span.bq-ft-company {{ new Date().getFullYear() }} BROADWAYINC PTE. LTD. Singapore.
            .bq-ft-right
                span.bq-ft-email support&#64;broadwayinc&#46;com
                .bq-ft-icons
                    a.bq-ft-icon(href="http://www.tiktok.com/@skapi_api" target="_blank" aria-label="TikTok" data-track="footer_tiktok_icon")
                        span.bq-ft-svg(:style="iconMask(iconTiktok)" aria-hidden="true")
                    a.bq-ft-icon(href="https://www.instagram.com/skapi_api" target="_blank" aria-label="Instagram" data-track="footer_instagram_icon")
                        span.bq-ft-svg(:style="iconMask(iconInstagram)" aria-hidden="true")
                    a.bq-ft-icon(href="https://www.youtube.com/@skapi_official" target="_blank" aria-label="YouTube" data-track="footer_youtube_icon")
                        span.bq-ft-svg(:style="iconMask(iconYoutube)" aria-hidden="true")
                    a.bq-ft-icon(href="https://x.com/skapi_api" target="_blank" aria-label="X" data-track="footer_x_icon")
                        span.bq-ft-svg(:style="iconMask(iconX)" aria-hidden="true")
                    a.bq-ft-icon(href="https://www.linkedin.com/company/skapi-backend-api/" target="_blank" aria-label="LinkedIn" data-track="footer_linkedin_icon")
                        span.bq-ft-svg(:style="iconMask(iconLinkedin)" aria-hidden="true")
                    a.bq-ft-icon(href="https://www.facebook.com/profile.php?id=61577236221327" target="_blank" aria-label="Facebook" data-track="footer_facebook_icon")
                        span.bq-ft-svg(:style="iconMask(iconFacebook)" aria-hidden="true")
                    a.bq-ft-icon(href="https://dev.to/skapi_api" target="_blank" aria-label="Dev.to" data-track="footer_devto_icon")
                        span.bq-ft-svg(:style="iconMask(iconDevto)" aria-hidden="true")
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { currentService } from "@/views/project-list";
import { version as APP_VERSION } from "../../package.json";

import iconTiktok from "@/assets/img/landingpage/icon_tiktok.svg";
import iconInstagram from "@/assets/img/landingpage/icon_instagram.svg";
import iconYoutube from "@/assets/img/landingpage/icon_youtube.svg";
import iconX from "@/assets/img/landingpage/icon_x.svg";
import iconLinkedin from "@/assets/img/landingpage/icon_linkedin.svg";
import iconFacebook from "@/assets/img/landingpage/icon_facebook.svg";
import iconDevto from "@/assets/img/landingpage/icon_devto.svg";

const router = useRouter();
const route = useRoute();
const hoveredMenu = ref("");
let currentRoutePath = ref(route.path);

const scrollSec = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
        const remOffset =
            4 * parseFloat(getComputedStyle(document.documentElement).fontSize);
        const scrollY = section.offsetTop - remOffset;
        window.scrollTo({ top: scrollY, behavior: "smooth" });
    }
};

// The social marks are painted as CSS masks (see .bq-ft-svg) so their colour
// comes from a theme token rather than a filter chain. The mask URL is the only
// per-icon part, so it rides in on a custom property.
const iconMask = (url) => ({ "--bq-ft-mask": `url("${url}")` });

// --footer-height is no longer what puts the footer at the bottom of the
// screen — App.vue's flex column does that with no measurement at all. It is
// still published because agent.vue subtracts it when it sizes the chat box,
// so it has to stay CORRECT: re-measure whenever the footer rewraps (window
// resize, a route that changes its contents) instead of only once on mount.
function setFooterHeight() {
    const footerElement = document.getElementById("footer");

    if (!footerElement) {
        return;
    }

    // Write 0px rather than bailing out when the footer is hidden (it is
    // display:none under 800px); skipping the write left consumers reading a
    // stale desktop height after a resize down.
    document.body.style.setProperty(
        "--footer-height",
        footerElement.offsetHeight + "px"
    );
}

let footerResizeObserver = null;

watch(
    () => route.path,
    (nv) => {
        let splitPath = nv.split("/");

        currentRoutePath.value = splitPath.length > 2 ? splitPath[1] : "";
    },
    { immediate: true }
);

onMounted(() => {
    setFooterHeight();

    const footerElement = document.getElementById("footer");

    if (footerElement && typeof ResizeObserver !== "undefined") {
        footerResizeObserver = new ResizeObserver(() => setFooterHeight());
        footerResizeObserver.observe(footerElement);
    } else {
        window.addEventListener("resize", setFooterHeight);
    }
});

onUnmounted(() => {
    footerResizeObserver?.disconnect();
    footerResizeObserver = null;
    window.removeEventListener("resize", setFooterHeight);
});
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";

#footer {
    position: relative;
    background: @paper;
    color: @ink;
    font-family: @mono;
    font-size: 0.85rem;
    border-top: 1px solid @line;
    z-index: 999;

    a {
        color: @ink;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }

    a.bq-ft-logo {
        text-decoration: none;

        &:hover {
            text-decoration: none;
        }
    }
}

/* ─── shared inner ─── */
.bq-ft-inner {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 2rem;
}

.bq-ft-logo {
    display: block;
    font-weight: 700;
    font-size: 1.05rem;
    letter-spacing: 0.04em;
    color: @ink;
    text-decoration: none;
    margin-bottom: 1rem;
}

/* ─── home footer: top ─── */
.bq-ft-top {
    padding: 2.5rem 0;
    border-bottom: 1px solid @line;

    .bq-ft-inner {
        align-items: flex-start;
    }
}

.bq-ft-left {
    display: flex;
    flex-wrap: wrap;
    // gap: 2rem;
    flex: 1;
    min-width: 0;
}

.bq-ft-sections,
.bq-ft-nav {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
        padding: 0.25rem 0;
        font-size: 0.85rem;
        cursor: pointer;
        color: @muted;
        transition: color 0.15s;

        &:hover {
            color: @ink;
        }

        a {
            color: inherit;
            text-decoration: none;

            &:hover {
                text-decoration: underline;
                color: @ink;
            }
        }
    }
}

.bq-ft-right {
    text-align: right;
}

.bq-ft-email {
    display: block;
    margin: 0 0 0.25rem;
    font-size: 0.85rem;
    color: @muted;
}

.bq-ft-terms {
    display: block;
    font-size: 0.8rem;
    color: @muted;
    cursor: pointer;

    &:hover {
        color: @ink;
    }
}

/* ─── home footer: bottom ─── */
.bq-ft-bottom {
    padding: 1.25rem 0;

    .bq-ft-inner {
        align-items: center;
    }
}

.bq-ft-company {
    font-size: 0.75rem;
    color: @muted;
}

.bq-ft-brand {
    display: flex;
    flex-direction: column;
    margin-right: 2rem;

    // Tighter than the default logo margin so the version sits snug beneath it.
    .bq-ft-logo {
        margin-bottom: 0.15rem;
    }
}

.bq-ft-version {
    font-size: 0.7rem;
    color: @muted;
    letter-spacing: 0.03em;
    opacity: 0.75;
}

.bq-ft-icons {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
}

.bq-ft-icon {
    display: inline-block;

    // Hover tints the mark with the brand accent. Because the colour is the
    // @pink TOKEN, it is #c2185b on the light scheme and #f06292 on the dark
    // one — the same accent the rest of the site uses. The hover used to be a
    // hand-tuned filter chain (invert/sepia/saturate/hue-rotate) that resolved
    // to a fixed #c42470: close to the light accent but not it, and frozen, so
    // dark mode kept painting the dark magenta onto the black footer.
    &:hover .bq-ft-svg {
        background-color: @pink;
        opacity: 1;
    }
}

.bq-ft-svg {
    display: block;
    width: 1.75rem;
    height: 1.75rem;
    // The social marks are monochrome, so they are drawn as a MASK and the
    // colour comes from background-color. That is what lets both the resting
    // and the hover colour be theme tokens; a filter() can never read one.
    // (The old code inverted the artwork with `--bq-icon-invert` to survive
    // dark mode — @ink already does that, being near-black on light paper and
    // near-white on dark.) The mask URL arrives per icon as --bq-ft-mask; see
    // iconMask() in the script block.
    background-color: @ink;
    opacity: 0.8;
    -webkit-mask-image: var(--bq-ft-mask);
    mask-image: var(--bq-ft-mask);
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;
    // Matches the `object-fit: contain` the <img> used to have.
    -webkit-mask-size: contain;
    mask-size: contain;
    transition: background-color 0.15s, opacity 0.15s;
}

/* ─── non-home footer ─── */
.bq-ft-wrap {
    padding: 1.875rem 2rem;
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    flex-wrap: wrap;
    gap: 0.5rem;

    .bq-ft-right {
        text-align: right;
    }
}

.bq-ft-info {
    .bq-ft-terms {
        margin-bottom: 0.25rem;
    }

    .bq-ft-company {
        display: block;
    }
}

/* ─── responsive ─── */
@media (max-width: 800px) {
    /* The footer does not render on mobile at all - the hamburger menu's own
       footer (navBar's .bq-mo-footer) carries the terms/social links there. Every
       other rule this media query used to hold styled DESCENDANTS of this hidden
       element (column layouts, the 1.25rem mobile gutter, the service-page 2px
       padding tweak) and so could never apply; they were leftovers from before
       the footer was hidden. If the mobile footer ever comes back, rebuild from
       the desktop rules plus the 1.25rem gutter the mobile bar uses. */
    #footer {
        display: none;
    }
}
</style>
